/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {DownloadJob} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {DeletePrompt} from '@ion/dls/src/editors/DeletePrompt';
import Error from './Error';
import Success from './Success';

// defines the TaskCancelPrompt Prop type definitions ...
export interface TaskCancelPromptProps {
  show: boolean;
  downloadjob: DownloadJob | null;
  result?: DownloadJob | null;
  loading?: boolean;
  error?: ServiceError | null;
  overrideMessage?: Function | null;
  onDeleteConfirm: (downloadjob: DownloadJob, index: number) => void;
  onDeleteCancel: () => void;
  index?: number;
  isCancelTask?: boolean;
}

/**
 * @TaskCancelPrompt
 *
 * The TaskCancelPrompt provides the UI for Cancelling an Job action.
 *
 * ```typescript
 *
 *    import {TaskCancelPrompt} from '@ion/dls/src/editors/';
 *
 *    const {
 *      selected,
 *      result,
 *      isEditing,
 *      loading,
 *      index,
 *      error
 *    } = useSelector(({downloadjobs: {cancelTask}}) => cancelTask);
 *
 *    const onDeleteConfirm = useCallback(function() {
 *      dispatch(cancelTask(selected, index));
 *    }, [selected, index]);
 *
 *    const onDeleteCancel = useCallback(function() {
 *      dispatch(cancelTasks(selected, index, false));
 *    }, [selected, index]);
 *
 *    return (
 *      <TaskCancelPrompt show={isEditing}
 *        downloadjob={selected}
 *        onDeleteConfirm={onDeleteConfirm}
 *        onDeleteCancel={onDeleteCancel}
 *        result={result}
 *        loading={loading}
 *        error={error} />
 *    );
 *
 * ```
 *
 * @param {boolean}       show When true, the job delete prompt will be displayed.
 * @param {DownloadJob}   job The selected Job that is to be deleted.
 * @param {DownloadJob}   result The Jov returned by the service after it has been deleted.
 * @param {boolean}       loading When true, indicates that the job is currently being
 *                        deleted.
 * @param {ServiceError}  error A reference to the ServiceError If the deletion fails.
 * @param {Function}      onDeleteConfirm callback function which will be invoked with the
 *                        job when the user confirms the deletion.
 * @param {Function}      onDeleteCancel callback function which will be invoked if the user
 *                        cancels the delete operation.
 */
export const TaskCancelPrompt = ({
  show,
  downloadjob,
  result,
  loading,
  error,
  index,
  onDeleteConfirm,
  onDeleteCancel,
  overrideMessage = null,
  isCancelTask = false
}: TaskCancelPromptProps) => {
  const taskName = downloadjob?.tasks?.[index]?.task?.metaData?.title;

  const onDelete = useCallback(function() {
    onDeleteConfirm(downloadjob, index);
  }, [downloadjob, index]);

  if (show || result || error) {
    return (
      <>
        {show && (
          <DeletePrompt show={show}
            entity={translate('downloadjobs:cancelTaskConfigurator')}
            items={downloadjob}
            isLoading={loading}
            isCancel={isCancelTask}
            titleMessage={true}
            overrideMessage={overrideMessage}
            onConfirm={onDelete}
            onCancel={onDeleteCancel} />
        )}
        <Error label={taskName}
          error={error} />
        <Success label={translate('downloadjobs:delete.taskSuccess')}
          result={result}
          loading={loading} />
      </>
    );
  }
  return null;
};

export default TaskCancelPrompt;
